import { createSelector } from 'reselect';

const getDocument = state => state.document;

export const getDocumentHistory = createSelector(getDocument, ({ history }) => history);

export const getAssignedDocument = createSelector(getDocument, ({ assigned }) => assigned);

export const getDocumentExtractionSessionId = createSelector(
  getDocument,
  ({ sessionId }) => sessionId,
);

export const getDocumentFilledExtractionFieldIds = createSelector(
  getDocument,
  ({ filledExtractionFieldIds }) => filledExtractionFieldIds,
);

export const selectDocumentFilledExtractionFields = createSelector(
  getDocument,
  ({ filledExtractionFields }) => filledExtractionFields,
);

/**
 * The documentId the currently loaded filledExtractionFields belong to.
 * Consumers must compare against their own documentId before trusting the
 * results — the store keeps only the last loaded document's results, so a
 * stale set from a previously inspected document must never render (PII guard)
 * nor be counted in the "N of M fields extracted" badge.
 */
export const selectFilledExtractionFieldsDocumentId = createSelector(
  getDocument,
  ({ filledExtractionFieldsDocumentId }) => filledExtractionFieldsDocumentId,
);

/**
 * Raw association id arrays for the current document.
 * Used by DocumentExtractionFieldsTab to determine which entities are linked
 * when showing the apply-bar on extraction field cards.
 */
export const selectDocumentAssociationIds = createSelector(getDocument, state => ({
  borrowerIds: state.borrowerIds ?? [],
  incomeIds: state.incomeIds ?? [],
  assetIds: state.assetIds ?? [],
  propertyIds: state.propertyIds ?? [],
  liabilityIds: state.liabilityIds ?? [],
}));

/**
 * Total count of entity links on the current document.
 * Sums the new collection-contract id arrays (borrowerIds etc.); falls back to
 * counting non-null scalar ids for documents not yet re-saved under the new contract.
 *
 * Used by DocumentInspector to show the count badge on the Links tab.
 */
export const selectDocumentTotalLinkCount = createSelector(getDocument, state => {
  const collectionTotal =
    (state.borrowerIds?.length ?? 0) +
    (state.incomeIds?.length ?? 0) +
    (state.assetIds?.length ?? 0) +
    (state.propertyIds?.length ?? 0) +
    (state.liabilityIds?.length ?? 0);

  if (collectionTotal > 0) {
    return collectionTotal;
  }

  // Fallback: legacy scalar ids for documents not yet re-saved under the new contract.
  return [
    state.borrowerId,
    state.incomeId,
    state.assetId,
    state.propertyId,
    state.liabilityId,
  ].filter(id => id != null).length;
});
